import React, { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useHistory } from "react-router-dom";

import RefreshActionButton from "components/atoms/refresh-action-button";
import { FixtureActionButton, FixturesShowcase, PageHeader, StatCard, StatsGridSkeleton } from "components/admin";
import {
  HeaderActions,
  SecondaryHeaderButton,
  StatsGrid,
} from "components/admin/styles/workspace-crud.styles";
import { useScopedSchoolSelection } from "hooks/use-scoped-school-selection";
import { SchoolMatchesSectionContentStack } from "pages/app/workspace/sections/styles/school-matches-section.inline.styles";
import { workspaceApi } from "pages/app/workspace/services";
import { hasPortalAction } from "utils/portal-access";

export const SchoolScoresheetsSection: React.FC<{
  embedded?: boolean;
  listBackTo?: string;
  portalAccess: any;
}> = ({ embedded = false, listBackTo = "/workspace/schedule?view=list&focus=scoring", portalAccess }) => {
  const history = useHistory();
  /*
    TODO(school-ops-ux): Scoresheets inherit the single-school filter from the global
    School Ops selector (see school-matches-section.tsx). Cross-school scoring desk
    behaviour should be revisited in the broader School Ops UX refactor.
  */
  const { schoolId } = useScopedSchoolSelection({
    portalAccess,
  });
  const matchesQuery = useQuery({
    queryKey: ["workspace-school-matches", schoolId],
    queryFn: () =>
      workspaceApi.schoolMatches.list({
        school_id: schoolId || undefined,
      }),
  });

  const canManageSchoolMatches = hasPortalAction(portalAccess, "school_matches.manage");
  const matches = useMemo(() => matchesQuery.data || [], [matchesQuery.data]);

  const scoreSummary = useMemo(() => {
    return matches.reduce(
      (summary: Record<string, number>, match: any) => {
        summary.total += 1;

        if (match.scoresheet_summary?.id) {
          summary.withScoresheet += 1;
        } else {
          summary.needsSetup += 1;
        }

        if (match.scoresheet_summary?.status === "live") {
          summary.live += 1;
        }

        return summary;
      },
      {
        live: 0,
        needsSetup: 0,
        total: 0,
        withScoresheet: 0,
      },
    );
  }, [matches]);

  return (
    <>
      {embedded ? null : (
        <PageHeader
          actions={
            <HeaderActions>
              <RefreshActionButton
                ButtonComponent={SecondaryHeaderButton}
                isLoading={matchesQuery.isLoading}
                label="Refresh"
                loadingLabel="Refreshing..."
                onClick={() => matchesQuery.refetch()}
              />
            </HeaderActions>
          }
          title="Scoresheets"
        />
      )}

      {matchesQuery.isLoading ? (
        <StatsGridSkeleton count={4} />
      ) : (
        <StatsGrid $tight={embedded}>
          <StatCard
            helper="All scoped fixtures currently available for live scoring."
            label="Fixtures"
            value={scoreSummary.total}
          />
          <StatCard
            helper="Matches with an existing score sheet attached."
            label="Active Sheets"
            value={scoreSummary.withScoresheet}
          />
          <StatCard
            helper="Matches currently marked live inside the scorer."
            label="Live Now"
            value={scoreSummary.live}
          />
          <StatCard
            helper="Fixtures that still need their first sheet started."
            label="Needs Setup"
            value={scoreSummary.needsSetup}
          />
        </StatsGrid>
      )}

      <SchoolMatchesSectionContentStack>
        <FixturesShowcase
          editLabel="View fixture"
          emptyDescription="Scoresheets will appear here once fixtures exist for the selected school scope."
          emptyTitle="No fixtures to score"
          fixtures={matches}
          isLoading={matchesQuery.isLoading}
          renderFixtureActions={(fixture) => (
            <>
              <FixtureActionButton
                $variant="link"
                onClick={() =>
                  history.push({
                    pathname: `/workspace/school-matches/${fixture.id}`,
                    state: {
                      backTo: listBackTo,
                    },
                  })
                }
                type="button"
              >
                Open match
              </FixtureActionButton>
              <FixtureActionButton
                $variant="primary"
                onClick={() =>
                  history.push({
                    pathname: `/workspace/school-scoresheets/${fixture.id}`,
                    state: {
                      backTo: listBackTo,
                    },
                  })
                }
                type="button"
              >
                {canManageSchoolMatches ? "Launch scorer" : "Review sheet"}
              </FixtureActionButton>
            </>
          )}
          title="Scoring Desk"
        />
      </SchoolMatchesSectionContentStack>
    </>
  );
};
